import { useCallback, useEffect, useState } from 'react'
import { useGlobalState } from '@highlight/ipc/react'
import * as Sentry from '@sentry/electron/renderer'
import { isMac } from '@shared/utils/platform'
import type { PermissionsState } from '@shared/types/AudioCapturePermissions'

interface UseMirroredPermissionOptions<T> {
  /** Field of the main-owned `permissions` mirror this hook tracks. Must be a module-level (stable) selector. */
  select: (permissions: PermissionsState) => T | undefined
  /** IPC read-without-prompt. Must be module-level; failures are captured and map to `fallback`. */
  checkPerm: () => Promise<T>
  /** Value `check` resolves to when the IPC read throws. */
  fallback: T
  /** Whether this permission is tracked at all (e.g. the resolved runtime needs it). */
  enabled: boolean
}

interface MirroredPermission<T> {
  status: T | undefined
  check: () => Promise<T>
  isLoaded: boolean
}

/**
 * Shared engine for the macOS capture permission hooks: reads the main-owned
 * `permissions` mirror (main refreshes it while a capture waits and on window
 * focus), fires one IPC check when the mirrored field has never been read, and
 * exposes a Sentry-guarded `check`. Request flows stay in the per-permission
 * hooks (`useMicrophonePermission`, `useSystemAudioPermission`,
 * `useScreenRecordingPermission`).
 */
export function useMirroredPermission<T>({
  select,
  checkPerm,
  fallback,
  enabled,
}: UseMirroredPermissionOptions<T>): MirroredPermission<T> {
  const [permissions] = useGlobalState<PermissionsState>('permissions')
  const [localFallback, setLocalFallback] = useState<T>()

  const check = useCallback(async (): Promise<T> => {
    try {
      const result = await checkPerm()
      setLocalFallback(undefined)
      return result
    } catch (error) {
      Sentry.captureException(error)
      setLocalFallback(fallback)
      return fallback
    }
  }, [checkPerm, fallback])

  const mirrored = permissions ? select(permissions) : undefined
  useEffect(() => {
    if (isMac && enabled && mirrored === undefined) {
      void check()
    }
  }, [check, enabled, mirrored])

  const status = enabled ? (mirrored ?? localFallback) : undefined

  return {
    status,
    check,
    /** The first read has resolved, or the permission isn't tracked here. */
    isLoaded: !isMac || !enabled || status !== undefined,
  }
}
